// <copyright file="quote-item-grouping-rows.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

/**
 * Presentation row model for the quote items table when item grouping is active.
 *
 * The table data stays a flat QuoteItem list (so the shared lit-table drag-and-drop keeps its
 * 1:1 index mapping); group headers are injected above their first member row through the
 * table's before-row-render hook, the same pattern the license-server deployments view uses for
 * its supplier sections.
 */

import type { QuoteItemGroupAttributes } from '../../../quotes/data/quote-item-grouping.js';
import { isGroupNode, isItemNode, isRenderedGroup } from '../../../quotes/data/quote-item-grouping.js';
import type { QuoteContainerManager } from '../../../quotes/data/quote-container.js';
import type { QuoteItem } from '../../../api/dealer-api-interface-quote.js';

export interface QuoteItemGroupHeader {
  /** the group id from the grouping document */
  id: string;
  name: string;
  attributes: QuoteItemGroupAttributes;
  /** commonIds of the member items that are live on the quote, in display order */
  itemCommonIds: string[];
}

export interface QuoteItemRowBuildResult {
  /** the table data: quote items only, in grouped display order */
  rows: QuoteItem[];
  /** item commonId -> containing group id, for items inside a group */
  groupIdByCommonId: Map<string, string>;
  /** row index -> group headers to render above that row (several when empty groups stack up) */
  headersBeforeIndex: Map<number, QuoteItemGroupHeader[]>;
  /** headers with no row below them to attach to (empty groups at the very end) */
  trailingHeaders: QuoteItemGroupHeader[];
}

/**
 * Build the table rows and header placements from the grouping document and the manager's sorted
 * item lists.
 *
 * Walks the grouping trunk in order; a group contributes a header (attached above its first member row)
 * followed by its member rows. Anything the document does not reference follows in its existing sorted
 * order, then the deleted items when requested. Without a grouping document the result is exactly the
 * legacy flat list with no headers.
 *
 * The document holds every live item, the supplier charge lines included, each already sitting at the
 * bottom of its container (see normalizeGroupingDocument). So a batch's own charge arrives here as one of
 * that group's children and needs no special handling: it renders under the group's header, after the
 * group's items, and it counts toward the header's item count - a batch of two windows plus its shipping
 * reads "3 items" and numbers 1, 2, 3. This walk used to have to find those rows itself, by asking the link
 * table which batch each charge belonged to.
 *
 * An empty PRIMARY batch is not rendered at all ({@link isRenderedGroup}); every other empty group keeps
 * its header, so the dealer has somewhere to drag items into.
 */
export function buildQuoteItemRows(quoteManager: QuoteContainerManager, showDeleted: boolean): QuoteItemRowBuildResult {
  // live = active + excluded-from-pricing; both are grouped/displayed, so the grouping doc may
  // reference either partition.
  const items = quoteManager.liveItems();
  const doc = quoteManager.quoteItemGrouping;
  const rows: QuoteItem[] = [];
  const groupIdByCommonId = new Map<string, string>();
  const headersBeforeIndex = new Map<number, QuoteItemGroupHeader[]>();

  // headers waiting for the next row to attach above; flushed whenever a row is added
  let pendingHeaders: QuoteItemGroupHeader[] = [];
  const addRow = (item: QuoteItem) => {
    if (pendingHeaders.length > 0) {
      headersBeforeIndex.set(rows.length, pendingHeaders);
      pendingHeaders = [];
    }
    rows.push(item);
  };

  if (!doc) {
    rows.push(...items);
  } else {
    const byCommonId = new Map(items.map(x => [x.commonId, x]));
    const referenced = new Set<string>();

    for (const node of doc.root) {
      if (isItemNode(node)) {
        const item = byCommonId.get(node.id);
        if (item) {
          addRow(item);
          referenced.add(item.commonId);
        }
        continue;
      }
      if (!isGroupNode(node) || !isRenderedGroup(node)) continue;

      const memberIds: string[] = [];
      const memberRows: QuoteItem[] = [];
      for (const child of node.children) {
        if (!isItemNode(child)) continue;
        const item = byCommonId.get(child.id);
        if (!item) continue;
        memberIds.push(item.commonId);
        memberRows.push(item);
        referenced.add(item.commonId);
        groupIdByCommonId.set(item.commonId, node.id);
      }

      pendingHeaders.push({
        id: node.id,
        name: node.name,
        attributes: node.attributes,
        itemCommonIds: memberIds
      });
      memberRows.forEach(addRow);
    }

    // items the document does not know: any straggler awaiting reconciliation, in its existing sorted order
    for (const item of items) {
      if (!referenced.has(item.commonId)) addRow(item);
    }
  }

  if (showDeleted) {
    (quoteManager.container.deletedItems ?? []).forEach(addRow);
  }

  return { rows, groupIdByCommonId, headersBeforeIndex, trailingHeaders: pendingHeaders };
}
